/*
 * Cmd-K-style navigation search — opens with `/` (or Ctrl/Cmd+K),
 * fuzzy-matches across the visible nav items in BOTH platform-admin
 * and workspace trees, arrow-keys to move, Enter to navigate.
 *
 * The search index is the union of items the current user can see
 * (filterNavTree from navigation/permissions). Workspace and platform
 * trees both carry into the index so platform admins can jump to any
 * workspace surface from anywhere — matches the Platform-Admin-sees-ALL
 * rule.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { NavItem, NavTree, PermissionResolver } from "../navigation/navigationTypes.js";
import { visibleItems } from "../navigation/permissions.js";

export interface NavigationSearchProps {
  trees: NavTree[];
  resolver: PermissionResolver;
}

interface IndexedItem extends NavItem {
  console: string;
  groupLabel: string;
  haystack: string;
}

function buildIndex(trees: NavTree[], resolver: PermissionResolver): IndexedItem[] {
  const out: IndexedItem[] = [];
  for (const tree of trees) {
    for (const group of tree.groups) {
      // visibleItems returns leaves only; we still need group label per
      // item for display, so re-walk here.
      for (const item of group.items) {
        out.push({
          ...item,
          console: tree.console,
          groupLabel: group.label,
          haystack: [item.label, item.description ?? "", group.label, ...(item.keywords ?? [])]
            .join(" ")
            .toLowerCase()
        });
      }
    }
  }
  // Filter through the visibility helper to honour permission gates.
  const visible = new Set(trees.flatMap((t) => visibleItems(t, resolver).map((i) => `${t.console}:${i.to}`)));
  return out.filter((i) => visible.has(`${i.console}:${i.to}`));
}

function score(query: string, item: IndexedItem): number {
  if (!query) return 0;
  const q = query.toLowerCase();
  if (item.label.toLowerCase() === q) return 100;
  if (item.label.toLowerCase().startsWith(q)) return 50;
  if (item.label.toLowerCase().includes(q)) return 30;
  if (item.haystack.includes(q)) return 10;
  return 0;
}

export default function NavigationSearch({ trees, resolver }: NavigationSearchProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const index = useMemo(() => buildIndex(trees, resolver), [trees, resolver]);
  const results = useMemo(() => {
    if (!query.trim()) return index.slice(0, 12);
    return index
      .map((i) => ({ item: i, score: score(query, i) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 12)
      .map((r) => r.item);
  }, [index, query]);

  // Global keyboard listeners — open on `/`, Cmd/Ctrl+K. Skip when a
  // text input already has focus so the user's own typing isn't hijacked.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const inField = target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(true);
        return;
      }
      if (e.key === "/" && !inField) {
        e.preventDefault();
        setOpen(true);
        return;
      }
      if (e.key === "Escape" && open) {
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      // Defer focus until after the modal mounts.
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  if (!open) return null;

  function pick(item: IndexedItem) {
    setOpen(false);
    navigate(item.to);
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = results[activeIndex];
      if (target) pick(target);
    }
  }

  return (
    <div className="nav-search-overlay" role="dialog" aria-modal="true" aria-label="Navigation search" onClick={() => setOpen(false)}>
      <div className="nav-search-modal" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          className="nav-search-input"
          type="text"
          placeholder="Search pages, settings, workspaces…"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
          onKeyDown={handleKey}
          aria-label="Search navigation"
        />
        <ul className="nav-search-results" role="listbox">
          {results.length === 0 && (
            <li className="nav-search-empty">No matching pages.</li>
          )}
          {results.map((item, i) => (
            <li
              key={`${item.console}:${item.to}`}
              role="option"
              aria-selected={i === activeIndex}
              className={`nav-search-result${i === activeIndex ? " active" : ""}`}
              onMouseEnter={() => setActiveIndex(i)}
              onClick={() => pick(item)}
            >
              <div className="nav-search-result-label">{item.label}</div>
              <div className="nav-search-result-meta">
                {item.console === "platform-admin" ? "Admin" : "Workspace"}
                {" · "}{item.groupLabel}
                {item.status === "coming-soon" && <span className="nav-search-coming"> · coming soon</span>}
              </div>
              {item.description && <div className="nav-search-result-desc">{item.description}</div>}
            </li>
          ))}
        </ul>
        <div className="nav-search-hint">
          <kbd>↑</kbd><kbd>↓</kbd> navigate · <kbd>Enter</kbd> open · <kbd>Esc</kbd> close
        </div>
      </div>
    </div>
  );
}
